// The HOUSEHOLD REPORT: what a husband sees about each of his wives — net worth (and the change since he last looked), liquid wealth, clicks,
// harvests, citizens, plots and buildings, when she was last active, her streak. It opens by itself once per sign-in (src/game/inviteManager.js
// emits HouseholdDue) unless he switched it off here, and from the Registry whenever he likes. Closing it marks it read, so the next report shows
// what changed from this moment. The numbers come from the server (GET /api/household): a wife's saved game is never sent to the husband's browser.

import './invites.css'
import { Events, on } from './eventBus.js'
import { proposalApi } from '../game/accountManager.js'
import { getProfile, persist } from '../game/state.js'
import { reportWanted } from '../game/inviteManager.js'
import { formatCoins } from './format.js'
import { regNo } from './invitePopup.js'
import { ord } from './proposalPopup.js'

const esc = (v) => String(v ?? '').replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]))
const n0 = (v) => Math.round(Number(v) || 0).toLocaleString('en-US')
export function ago(t, now = Date.now()) {
  if (!t) return 'a long time ago'
  const m = Math.max(0, Math.round((now - t) / 60000))
  if (m < 2) return 'just now'
  if (m < 90) return `${m} min ago`
  const h = Math.round(m / 60)
  if (h < 36) return `${h} h ago`
  return `${Math.round(h / 24)} days ago`
}
const delta = (v, fmt = n0) => (v == null ? '' : v > 0 ? ` <span class="hh-up">▲ +${fmt(v)}</span>` : v < 0 ? ` <span class="hh-down">▼ −${fmt(-v)}</span>` : ' <span class="hh-flat">no change</span>')

// The report as HTML (also used by tests). `r` is GET /api/household.
export function reportHtml(r) {
  const now = r.now ?? Date.now()
  const since = r.wives.map((w) => w.seenAt).filter(Boolean).sort((a, b) => b - a)[0]
  return `
    <p class="hh-sub">${r.wives.length} ${r.wives.length === 1 ? 'wife' : 'wives'}. ${since ? `Changes are shown since you last looked, ${esc(ago(since, now))}.` : 'This is your first report — from now on it shows what changed since you last looked.'}</p>
    <div class="hh-total"><div><b>${esc(formatCoins(r.combinedWealth))}</b><br><span>Combined wealth</span></div><div><b>${esc(formatCoins(r.ownNetWorth))}</b><br><span>Your net worth</span></div><div><b>${esc(formatCoins(r.wivesTotal))}</b><br><span>Your wives together</span></div></div>
    <ul class="hh-wives">${r.wives.map((w, i) => `<li class="hh-wife" data-wife="${esc(w.wife.id)}">
      <h3>${esc(w.wife.name)} <small>${ord(i + 1)} wife · No. ${regNo(w.wife.registryNo)} · ${esc(w.wife.kingdom ?? '')} · ${esc(w.wife.title ?? '')}</small></h3>
      <p class="hh-since">Married ${esc(new Date(w.marriedAt).toLocaleDateString(undefined, { day: 'numeric', month: 'long', year: 'numeric' }))} · last active ${esc(ago(w.stats.lastActive, now))}</p>
      <dl class="hh-grid">
        <div><dt>Net worth</dt><dd>${esc(formatCoins(w.stats.netWorth))}${delta(w.changes?.netWorth, formatCoins)}</dd></div>
        <div><dt>Coin on hand</dt><dd>${esc(formatCoins(w.stats.wealth))}${delta(w.changes?.wealth, formatCoins)}</dd></div>
        <div><dt>Clicks</dt><dd>${n0(w.stats.clicks)}${delta(w.changes?.clicks)}</dd></div>
        <div><dt>Harvests</dt><dd>${n0(w.stats.harvests)}${delta(w.changes?.harvests)}</dd></div>
        <div><dt>Citizens</dt><dd>${n0(w.stats.citizens)}${delta(w.changes?.citizens)}</dd></div>
        <div><dt>Plots · buildings</dt><dd>${n0(w.stats.plots)} · ${n0(w.stats.buildings)}</dd></div>
        <div><dt>Day streak</dt><dd>${n0(w.stats.streak)}</dd></div>
      </dl></li>`).join('')}</ul>`
}

let layer = null
export async function openHouseholdReport({ auto = false } = {}) {
  if (document.querySelector('.hh-layer')) return
  let r
  try { r = await proposalApi.household() } catch { return }
  if (!r.wives?.length) { if (!auto) alert('You have no wives yet — your household report appears here once you are married.'); return }
  layer = document.createElement('div'); layer.className = 'inv-layer hh-layer'
  layer.innerHTML = `<div class="inv-pop"><div class="inv-card hh-card" role="dialog" aria-modal="true" aria-labelledby="hh-title">
      <div class="inv-kicker">Household report</div>
      <h2 id="hh-title">Your wives</h2>
      <div data-hh-body>${reportHtml(r)}</div>
      <label class="hh-pref"><input type="checkbox" data-hh-pref ${reportWanted() ? 'checked' : ''} /> Show this report when I sign in</label>
      <div class="inv-actions"><button type="button" class="inv-btn inv-btn--main" data-hh="close">Mark as read</button></div>
    </div></div>`
  document.body.appendChild(layer)
  layer.querySelector('[data-hh]')?.focus()
  const finish = async () => { const l = layer; layer = null; l?.remove(); try { await proposalApi.householdSeen() } catch { /* the next report just shows a longer period */ } }
  layer.addEventListener('click', (e) => { if (e.target.closest('[data-hh="close"]')) finish() })
  layer.addEventListener('change', (e) => { if (e.target.matches('[data-hh-pref]')) { getProfile().householdReport = e.target.checked; persist() } })
  layer.addEventListener('keydown', (e) => { if (e.key === 'Escape') finish() })
}

export function mountHouseholdReport() {
  on(Events.HouseholdDue, () => setTimeout(() => { if (!document.querySelector('.inv-pop')) openHouseholdReport({ auto: true }); else setTimeout(() => openHouseholdReport({ auto: true }), 4000) }, 1500)) // (after a sign-in pop-up, if any)
}
